import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n';
import useSupportStore from '../stores/supportStore';

const SupportChatWidget = () => {
  const { user, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const { tickets, createTicket, addUserMessage, getUserTicket } = useSupportStore();

  const [isOpen, setIsOpen] = useState(false);
  const [currentTicketId, setCurrentTicketId] = useState(null);
  const [autoResponseSentToday, setAutoResponseSentToday] = useState(false);
  const [localMessages, setLocalMessages] = useState([
    {
      id: 1,
      from: 'support',
      text: t('support.chat.welcome'),
      time: new Date().toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' }),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);
  const lastMessageCountRef = useRef(0);

  // Check if auto-response was already sent today for this user
  useEffect(() => {
    if (user?.id) {
      const storageKey = `supportChatAutoResponseDate_${user.id}`;
      const stored = localStorage.getItem(storageKey);
      const today = new Date().toDateString();
      setAutoResponseSentToday(stored === today);
    }
  }, [user?.id]);

  // Find or sync with existing ticket when user is authenticated
  useEffect(() => {
    const loadUserTicket = async () => {
      if (isAuthenticated && user?.email && isOpen) {
        const existingTicket = await getUserTicket(user.id, user.email);
        if (existingTicket) {
          setCurrentTicketId(existingTicket.id);
          // Sync messages from ticket
          const ticketMessages = existingTicket.messages.map(msg => ({
            id: msg.id,
            from: msg.sender === 'admin' ? 'support' : 'user',
            text: msg.text,
            time: msg.time.split(' ')[1] || msg.time,
          }));
          setLocalMessages([
            {
              id: 'welcome',
              from: 'support',
              text: t('support.chat.welcome'),
              time: new Date().toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' }),
            },
            ...ticketMessages
          ]);
        }
      }
    };
    loadUserTicket();
  }, [isAuthenticated, user, isOpen, getUserTicket, t]);

  // Sync messages from store when ticket updates. Only rewrite localMessages
  // when the message list actually changed — the 3s poller keeps replacing
  // `tickets` identity even when there is nothing new, and rewriting on every
  // tick used to force the auto-scroll effect to fire and hijack the user's
  // scroll position mid-read.
  useEffect(() => {
    if (!currentTicketId) return;
    const ticket = tickets.find(t => t.id === currentTicketId);
    if (!ticket) return;

    setLocalMessages((prev) => {
      const ticketMessages = ticket.messages.map(msg => ({
        id: msg.id,
        from: msg.sender === 'admin' ? 'support' : 'user',
        text: msg.text,
        time: msg.time.split(' ')[1] || msg.time,
      }));
      const prevIds = prev.filter(m => m.id !== 'welcome').map(m => String(m.id)).join('|');
      const nextIds = ticketMessages.map(m => String(m.id)).join('|');
      if (prevIds === nextIds) return prev;
      return [
        {
          id: 'welcome',
          from: 'support',
          text: t('support.chat.welcome'),
          time: new Date().toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' }),
        },
        ...ticketMessages,
      ];
    });
  }, [tickets, currentTicketId, t]);

  // Poll for new messages when chat is open. Depend on the primitive id/email
  // rather than the whole `user` object so a re-render of AuthContext does not
  // tear down and recreate the interval.
  useEffect(() => {
    if (!isOpen || !isAuthenticated || !user?.email) return;

    const uid = user.id;
    const email = user.email;
    const pollMessages = async () => {
      const ticket = await getUserTicket(uid, email);
      if (ticket) {
        setCurrentTicketId(ticket.id);
      }
    };

    const intervalId = setInterval(pollMessages, 3000); // Poll every 3 seconds

    return () => clearInterval(intervalId);
  }, [isOpen, isAuthenticated, user?.id, user?.email, getUserTicket]);

  // Only auto-scroll when a new message actually arrives; skip when the
  // effect fires from an identity-only re-render, otherwise the user cannot
  // scroll up to read history while the widget polls.
  useEffect(() => {
    if (localMessages.length > lastMessageCountRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    lastMessageCountRef.current = localMessages.length;
  }, [localMessages]);

  const handleSend = async () => {
    const text = inputValue.trim();
    if (!text) return;

    const time = new Date().toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });

    // Add message to local state immediately for responsiveness
    const userMessage = {
      id: Date.now(),
      from: 'user',
      text,
      time,
    };
    setLocalMessages((prev) => [...prev, userMessage]);
    setInputValue('');

    // Save to store
    const userData = {
      id: user?.id,
      name: user?.name || user?.surname ? `${user?.name || ''} ${user?.surname || ''}`.trim() : t('support.chat.anonymousUser'),
      email: user?.email || 'anonymous@user.com',
      role: user?.role || 'student'
    };

    if (currentTicketId) {
      // Add to existing ticket
      await addUserMessage(currentTicketId, text);
    } else {
      // Create new ticket
      const newTicket = await createTicket(userData, text);
      if (newTicket) {
        setCurrentTicketId(newTicket.id);
      }
    }

    // Show auto-response only once per day (after first message of the day)
    if (!autoResponseSentToday) {
      setTimeout(() => {
        setLocalMessages((prev) => [
          ...prev,
          {
            id: Date.now() + 1,
            from: 'support',
            text: t('support.chat.autoResponse'),
            time: new Date().toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' }),
          },
        ]);
      }, 1000);
      const today = new Date().toDateString();
      const storageKey = `supportChatAutoResponseDate_${user?.id || 'anonymous'}`;
      localStorage.setItem(storageKey, today);
      setAutoResponseSentToday(true);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      className="fixed right-4 md:right-6 bottom-[calc(5rem+env(safe-area-inset-bottom))] md:bottom-6 z-50"
    >
      {isOpen && (
        <div className="mb-4 w-[calc(100vw-2rem)] max-w-sm sm:w-96 bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200"
             style={{ height: 'min(28rem, calc(100vh - 10rem))' }}>
          {/* Header */}
          <div className="bg-brand-primary text-white px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
                <MessageCircle size={18} />
              </div>
              <div>
                <p className="font-semibold text-sm">{t('support.chat.headerTitle')}</p>
                <p className="text-xs text-white/70">{t('support.chat.online')}</p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 rounded-full p-1 transition">
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-brand-cream/30">
            {localMessages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm ${
                  msg.from === 'user'
                    ? 'bg-brand-primary text-white rounded-br-md'
                    : 'bg-white text-gray-800 shadow-sm rounded-bl-md'
                }`}>
                  <p>{msg.text}</p>
                  <p className={`text-[10px] mt-1 ${msg.from === 'user' ? 'text-white/60' : 'text-gray-400'}`}>
                    {msg.time}
                  </p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 px-4 py-3 flex items-center gap-2 bg-white">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('support.chat.inputPlaceholder')}
              className="flex-1 text-sm px-3 py-2 rounded-xl border border-gray-200 focus:outline-none focus:border-brand-primary transition"
            />
            <button
              onClick={handleSend}
              disabled={!inputValue.trim()}
              className="bg-brand-primary hover:bg-brand-primary/90 disabled:opacity-40 text-white p-2.5 rounded-xl transition"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Toggle Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="bg-brand-primary hover:bg-brand-primary/90 text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-105"
        >
          <MessageCircle size={24} />
        </button>
      )}
    </div>
  );
};

export default SupportChatWidget;
